import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { isVideoFilename } from "../../../utils";
import { MediaPreviewCard } from "../../components/MediaPreviewCard";
import { WatchTogetherForm } from "./WatchTogetherForm";
import type { MediaItem } from "../../../types";

// Portals into #watchTogetherView - the dedicated "Watch Together" page for
// starting/scheduling a session from Home, where (unlike Lightbox.tsx's
// own trigger) no video is already open. Picking a video here just feeds
// the exact same WatchTogetherForm/togetherApi.ts engine the existing
// modal uses - there is one Watch Together implementation, not two.
export function WatchTogetherScreen() {
  const navigate = useNavigate();
  const media = useAppState((s) => s.media);
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);

  const anchor = getVanillaEl<HTMLDivElement>("watchTogetherView");
  if (!anchor) return null;

  const videos = media.filter((m) => isVideoFilename(m.filename));

  function toggleSelect(item: MediaItem): void {
    setSelectedFilename((current) => (current === item.filename ? null : item.filename));
  }

  return createPortal(
    <>
      <div className="chat-thread-header d-flex align-items-center gap-2 mb-3">
        <button type="button" className="btn btn-outline-pink btn-sm" aria-label="Back to Home" onClick={() => navigate("/")}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <h2 className="h6 mb-0 flex-grow-1">
          <i className="bi bi-film me-2 text-pink" aria-hidden="true" />
          Watch Together
        </h2>
      </div>

      <p className="small muted mb-3">Pick something to watch together.</p>

      {!videos.length ? (
        <p className="small muted mb-3">No videos yet. Add a video to your gallery before starting Watch Together.</p>
      ) : (
        <div className="watch-together-grid mb-3">
          {videos.map((item) => (
            <div key={item.filename} className="watch-together-tile-wrap">
              <MediaPreviewCard item={item} onView={() => toggleSelect(item)} className={selectedFilename === item.filename ? "is-selected" : ""} />
              {selectedFilename === item.filename && (
                <span className="watch-together-tile-check" aria-hidden="true">
                  <i className="bi bi-check-circle-fill" />
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <WatchTogetherForm
        key={selectedFilename ?? "none"}
        filename={selectedFilename}
        mediaType="video"
        onDone={() => {
          setSelectedFilename(null);
          navigate("/");
        }}
      />
    </>,
    anchor,
  );
}
